/**
 * Tiered PDP upsell: CRO layout tier selection, delta/strike row prices,
 * ADD / REMOVE bundle CTAs, cart sync via data-selected-addon-variant-ids.
 */

import { ThemeEvents } from '@theme/events';

/** Main product only: no add-on radios selected (not a visible tier card). */
const NO_ADDON_TIER_ID = 'main';

/**
 * @typedef {object} TierAddon
 * @property {string} variantId
 * @property {number} price
 * @property {number} compareAt
 * @property {boolean} available
 * @property {string} productId
 */

/**
 * @typedef {object} TierConfig
 * @property {string} id
 * @property {string} label
 * @property {string} [subtitle]
 * @property {string} badge
 * @property {boolean} [hasAddons]
 * @property {number} [priceDeductionCents] Smallest-currency-unit discount on add-on total (display + summary).
 * @property {number} [addonDeltaCents]
 * @property {number} [addonStrikeCents]
 * @property {TierAddon[]} addons
 */

/**
 * @typedef {object} UpsellModuleConfig
 * @property {string} blockId
 * @property {TierConfig[]} tiers Add-on tiers only (e.g. tier-2, tier-3). Main-only state uses NO_ADDON_TIER_ID in JS.
 * @property {{ id: string; price: number; compareAt: number; available: boolean }} initialMainVariant
 */

/**
 * @param {number} cents
 * @returns {string}
 */
function formatMoneyCents(cents) {
  const n = Number(cents);
  if (Number.isNaN(n)) return '';
  if (typeof window.Shopify !== 'undefined' && typeof window.Shopify.formatMoney === 'function') {
    return window.Shopify.formatMoney(n);
  }
  return (n / 100).toFixed(2);
}

/**
 * Tier row "+£35" style delta: whole currency units (no decimals) + symbol.
 * @param {number} cents
 * @param {HTMLElement} root
 * @returns {string}
 */
function formatTierDeltaCents(cents, root) {
  const n = Number(cents);
  if (Number.isNaN(n)) return '';

  const formatTemplate = (root.getAttribute('data-tier-delta-money-format') || '').trim();
  const currencySymbol = (root.getAttribute('data-tier-delta-currency-symbol') || '').trim();

  if (typeof window.Shopify !== 'undefined' && typeof window.Shopify.formatMoney === 'function') {
    if (formatTemplate) {
      return window.Shopify.formatMoney(n, formatTemplate);
    }
    const formatted = window.Shopify.formatMoney(n);
    return formatted.replace(/([.,]00)(?!\d)/, '').replace(/([.,]00)$/, '');
  }

  const whole = Math.round(n / 100);
  return currencySymbol ? `${currencySymbol}${whole}` : String(whole);
}

/**
 * @param {HTMLElement} root
 */
function bindPriceDisplay(root) {
  const totalEl = root.querySelector('[data-tiered-price-total]');
  const compareRow = root.querySelector('[data-tiered-price-compare-row]');
  const compareEl = root.querySelector('[data-tiered-price-compare]');
  const savingsEl = root.querySelector('[data-tiered-price-savings]');
  const savingsPctEl = root.querySelector('[data-tiered-price-savings-pct]');

  return {
    /**
     * @param {{ total: number; compareTotal: number; showSavings: boolean; showPercent: boolean }} p
     */
    render(p) {
      const savingsPrefix = (root.getAttribute('data-savings-prefix') || '').trim();
      if (totalEl) totalEl.textContent = formatMoneyCents(p.total);
      const hasCompare = p.compareTotal > p.total && p.compareTotal > 0;
      if (compareRow) compareRow.hidden = !hasCompare;
      if (compareEl) compareEl.textContent = formatMoneyCents(p.compareTotal);
      if (!p.showSavings) {
        if (savingsEl) savingsEl.textContent = '';
        if (savingsPctEl) savingsPctEl.textContent = '';
        return;
      }
      const abs = p.compareTotal - p.total;
      if (savingsEl) {
        if (hasCompare && abs > 0) {
          const parts = [savingsPrefix, formatMoneyCents(abs)].filter(Boolean);
          savingsEl.textContent = parts.join(' ');
        } else {
          savingsEl.textContent = '';
        }
      }
      if (savingsPctEl) {
        if (!p.showPercent || !hasCompare || abs <= 0) {
          savingsPctEl.textContent = '';
        } else {
          const pct = Math.round((abs / p.compareTotal) * 100);
          savingsPctEl.textContent = pct > 0 ? `(${pct}%)` : '';
        }
      }
    },
  };
}

/**
 * @param {HTMLElement} root
 * @param {TierConfig[]} tiers
 */
function syncTierRowPrices(root, tiers, mainVariant) {
  tiers.forEach((tier) => {
    const { total } = totalsForTier(tier, mainVariant);
    const fullEl = root.querySelector(`[data-tier-price-row="${tier.id}"]`);
    if (fullEl) fullEl.textContent = formatMoneyCents(total);

    const deltaEl = root.querySelector(`[data-tier-delta="${tier.id}"]`);
    const strikeEl = root.querySelector(`[data-tier-strike="${tier.id}"]`);
    if (!tier.hasAddons || !deltaEl) return;

    const delta = Number(tier.addonDeltaCents) || 0;
    const strike = Number(tier.addonStrikeCents) || 0;
    deltaEl.textContent = delta > 0 ? `+${formatTierDeltaCents(delta, root)}` : '';
    if (strikeEl) {
      if (strike > delta) {
        strikeEl.textContent = formatMoneyCents(strike);
        strikeEl.hidden = false;
      } else {
        strikeEl.textContent = '';
        strikeEl.hidden = true;
      }
    }
  });
}

/**
 * @param {TierConfig | undefined} tier
 * @param {{ price: number; compareAt: number }} mainVariant
 */
function totalsForTier(tier, mainVariant) {
  const mainPrice = mainVariant.price;
  const mainCompare = Math.max(mainVariant.compareAt, mainPrice);
  let addPrice = 0;
  let addCompare = 0;
  tier?.addons.forEach((a) => {
    const p = Number(a.price) || 0;
    const c = Number(a.compareAt) || p;
    addPrice += p;
    addCompare += Math.max(c, p);
  });
  const rawDeduction = Number(tier?.priceDeductionCents) || 0;
  const deduction = Math.min(Math.max(rawDeduction, 0), addPrice);
  const total = mainPrice + addPrice - deduction;
  const compareTotal = mainCompare + addCompare;
  return { total, compareTotal };
}

/**
 * @param {HTMLElement} root
 * @param {string} selectedTierId
 */
function syncAriaRadios(root, selectedTierId) {
  root.querySelectorAll('[data-tiered-tier-card][role="radio"]').forEach((el) => {
    const id = el.getAttribute('data-tier-id');
    const checked = id === selectedTierId;
    el.setAttribute('aria-checked', checked ? 'true' : 'false');
    el.classList.toggle('tiered-pdp-upsell__tier--selected', checked);
  });
}

class UpsellSelector {
  /**
   * @param {HTMLElement} root
   * @param {UpsellModuleConfig} config
   * @param {ReturnType<typeof bindPriceDisplay>} priceDisplay
   */
  constructor(root, config, priceDisplay) {
    this.root = root;
    this.config = config;
    /** @type {TierConfig[]} */
    this.addonTiers = config.tiers || [];
    this.priceDisplay = priceDisplay;
    this.selectedTierId = NO_ADDON_TIER_ID;
    /** @type {{ id: string; price: number; compareAt: number; available: boolean }} */
    this.mainVariant = { ...config.initialMainVariant };
    this.showSavings = root.getAttribute('data-show-savings') === 'true';
    this.showPercent = root.getAttribute('data-show-savings-percent') === 'true';
    this.gateEl = root.querySelector('[data-tiered-variant-gate]');
    this.sizeErrorEl = root.querySelector('[data-tiered-size-error]');
    this.#bindInputs();
    this.#bindCtas();
    this.#bindCardClicks();
    this.#bindKeyboard();
    this.#bindVariantUpdates();
    this.applyTier(this.selectedTierId, { silent: true });
  }

  #setRadioChecked(tierId) {
    if (tierId === NO_ADDON_TIER_ID) {
      this.applyTier(NO_ADDON_TIER_ID);
      return;
    }
    if (!this.#isMainSizeSelected()) {
      this.#showSizeError();
      return;
    }
    let radio = null;
    this.root.querySelectorAll('input[data-tiered-tier-input]').forEach((inp) => {
      if (inp instanceof HTMLInputElement && inp.value === tierId && !inp.disabled) radio = inp;
    });
    if (radio) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  #bindInputs() {
    this.root.querySelectorAll('input[data-tiered-tier-input]').forEach((input) => {
      input.addEventListener('change', () => {
        if (input instanceof HTMLInputElement && input.checked) {
          this.applyTier(input.value);
        }
      });
    });
  }

  #bindCtas() {
    this.root.querySelectorAll('[data-tier-pick]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const id = btn.getAttribute('data-tier-pick');
        if (!id) return;
        this.#setRadioChecked(id);
      });
    });
  }

  #bindCardClicks() {
    this.root.querySelectorAll('[data-tiered-tier-card]').forEach((card) => {
      card.addEventListener('click', (e) => {
        const t = e.target;
        if (t instanceof Element && t.closest('[data-tier-pick]')) return;
        if (t instanceof HTMLInputElement) return;
        const id = card.getAttribute('data-tier-id');
        if (!id) return;
        if (!this.#isMainSizeSelected()) {
          this.#showSizeError();
          return;
        }
        let radio = null;
        this.root.querySelectorAll('input[data-tiered-tier-input]').forEach((inp) => {
          if (inp instanceof HTMLInputElement && inp.value === id) radio = inp;
        });
        if (radio instanceof HTMLInputElement && !radio.disabled) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    });
  }

  #bindKeyboard() {
    this.root.querySelectorAll('[data-tiered-tier-card][role="radio"]').forEach((card) => {
      card.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        const id = card.getAttribute('data-tier-id');
        if (!id) return;
        this.#setRadioChecked(id);
      });
    });
  }

  #bindVariantUpdates() {
    const section = this.root.closest('.shopify-section');
    if (!section) return;
    section.addEventListener(
      ThemeEvents.variantUpdate,
      /** @param {CustomEvent} e */ (e) => {
        const v = e.detail?.resource;
        if (!v || typeof v.price !== 'number') return;
        const formProductId = e.detail?.data?.productId;
        const mainPid = this.root.getAttribute('data-main-product-id');
        if (formProductId && mainPid && String(formProductId) !== String(mainPid)) return;
        this.mainVariant = {
          id: String(v.id),
          price: v.price,
          compareAt:
            typeof v.compare_at_price === 'number' && v.compare_at_price > v.price
              ? v.compare_at_price
              : v.price,
          available: Boolean(v.available),
        };
        this.applyTier(this.selectedTierId, { silent: true });
        if (this.gateEl && this.mainVariant.available) {
          this.gateEl.hidden = true;
        }
        this.#hideSizeError();
      }
    );
  }

  /**
   * Reads the main product form's hidden variant input. Empty value means no
   * size/variant has been selected yet (matches buy-buttons.liquid behaviour).
   * @returns {boolean}
   */
  #isMainSizeSelected() {
    const section = this.root.closest('.shopify-section');
    if (!section) return true;
    const mainPid = this.root.getAttribute('data-main-product-id');
    let form = null;
    if (mainPid) {
      form = section.querySelector(`product-form-component[data-product-id="${mainPid}"]`);
    }
    if (!form) {
      form = section.querySelector('product-form-component');
    }
    if (!form) return true;
    const input = form.querySelector('input[name="id"]');
    if (!input) return true;
    return Boolean(input.value && String(input.value).trim().length > 0);
  }

  #showSizeError() {
    if (this.sizeErrorEl) this.sizeErrorEl.hidden = false;
  }

  #hideSizeError() {
    if (this.sizeErrorEl) this.sizeErrorEl.hidden = true;
  }

  #maybeToggleGate(tierId) {
    if (!this.gateEl) return;
    const tier =
      tierId === NO_ADDON_TIER_ID
        ? { hasAddons: false }
        : this.addonTiers.find((t) => t.id === tierId);
    const wantsBundle = Boolean(tier?.hasAddons);
    if (wantsBundle && !this.mainVariant.available) {
      this.gateEl.hidden = false;
    } else {
      this.gateEl.hidden = true;
    }
  }

  /**
   * @param {string} tierId
   * @param {{ silent?: boolean }} [opts]
   */
  applyTier(tierId, opts = {}) {
    if (tierId === NO_ADDON_TIER_ID) {
      this.root.querySelectorAll('input[data-tiered-tier-input]').forEach((inp) => {
        if (inp instanceof HTMLInputElement) inp.checked = false;
      });
    }

    const tier =
      tierId === NO_ADDON_TIER_ID
        ? { id: NO_ADDON_TIER_ID, addons: [], hasAddons: false }
        : this.addonTiers.find((t) => t.id === tierId);
    if (!tier) return;

    if (tierId !== NO_ADDON_TIER_ID) {
      const unavailable = tier.addons.some((a) => !a.available);
      if (unavailable) {
        this.applyTier(NO_ADDON_TIER_ID, opts);
        return;
      }
    }

    this.selectedTierId = tierId;
    const addonIds = tier.addons.map((a) => a.variantId).filter(Boolean);
    this.root.setAttribute('data-selected-tier-id', tierId);
    this.root.setAttribute('data-selected-addon-variant-ids', addonIds.join(','));

    const { total, compareTotal } = totalsForTier(tier, this.mainVariant);
    this.root.setAttribute('data-preview-total-cents', String(total));

    syncAriaRadios(this.root, tierId);

    this.priceDisplay.render({
      total,
      compareTotal,
      showSavings: this.showSavings,
      showPercent: this.showPercent,
    });

    syncTierRowPrices(this.root, this.addonTiers, this.mainVariant);

    this.#maybeToggleGate(tierId);
  }
}

function initUpsellContainers() {
  document.querySelectorAll('[data-tiered-upsell]').forEach((root) => {
    if (!(root instanceof HTMLElement)) return;
    if (root.getAttribute('data-tiered-upsell-initialized') === 'true') return;
    const script = root.querySelector('script[type="application/json"][data-tiered-upsell-config]');
    if (!script?.textContent) return;
    /** @type {UpsellModuleConfig | null} */
    let config = null;
    try {
      config = JSON.parse(script.textContent);
    } catch {
      return;
    }
    if (!config?.tiers?.length) return;
    root.setAttribute('data-tiered-upsell-initialized', 'true');
    const priceDisplay = bindPriceDisplay(root);
    const selector = new UpsellSelector(root, config, priceDisplay);
    Object.defineProperty(root, '__tieredPdpUpsell', { value: selector, configurable: true });
  });
}

document.addEventListener('DOMContentLoaded', initUpsellContainers);
document.addEventListener('shopify:section:load', initUpsellContainers);
